// connectionState.ts
// ———————————————————————————————————————————————————————————————
// Gestión del estado de conexión SCORM y notificaciones
// ———————————————————————————————————————————————————————————————

import type { ScormConnectionState, ConnectionListener } from './types';

class ConnectionStateManager {
  private state: ScormConnectionState = {
    isConnected: false,
    hasAttemptedInit: false,
    version: null,
    errorMessage: null,
    initTimestamp: null,
  };

  private listeners: Set<ConnectionListener> = new Set();

  /**
   * Actualiza el estado de conexión
   */
  setState(newState: Partial<ScormConnectionState>): void {
    this.state = { ...this.state, ...newState };
    this.notifyListeners();
  }

  /**
   * Obtiene una copia del estado actual
   */
  getState(): ScormConnectionState {
    return { ...this.state };
  }

  /**
   * Verifica si está conectado
   */
  isConnected(): boolean {
    return this.state.isConnected;
  }

  /**
   * Registra un listener para cambios de estado
   */
  addListener(listener: ConnectionListener): void {
    this.listeners.add(listener);
  }

  /**
   * Elimina un listener
   */
  removeListener(listener: ConnectionListener): void {
    this.listeners.delete(listener);
  }

  /**
   * Notifica a todos los listeners sobre cambios de estado
   */
  private notifyListeners(): void {
    this.listeners.forEach((listener) => {
      try {
        listener({ ...this.state });
      } catch (error) {
        console.error("Error en listener de conexión SCORM:", error);
      }
    });
  }

  /**
   * Reinicia el estado de conexión
   */
  reset(): void {
    this.state = {
      isConnected: false,
      hasAttemptedInit: false,
      version: null,
      errorMessage: null,
      initTimestamp: null,
    };
    this.notifyListeners();
  }
}

// Singleton para gestión de estado
export const connectionStateManager = new ConnectionStateManager();
